import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { TopUp, Student } from '../types'
import { Plus, DollarSign, CreditCard, Building, Banknote, FileDown, Filter, ChevronDown, Pencil, Trash2, X, AlertTriangle, Hash, Calculator, Timer, Info } from 'lucide-react'
import { format } from 'date-fns'
import { ActivityClock } from '../components/ActivityClock'
import { logActivity, createNotification, notifyLowBalance } from '../lib/activity'
import { hasAdminRights } from '../lib/roles'
import { fromSessions, fromAmount, getSessionLength, formatMoney } from '../lib/pricing'

interface ExtendedTopUp extends TopUp {
  studentName?: string
}

type InputMode = 'sessions' | 'amount' | 'manual'

const emptyForm = {
  studentId: '',
  inputMode: 'sessions' as InputMode,
  sessions: '',
  minutesAdded: 0,
  amountUsd: 0,
  method: 'card' as 'card' | 'bank' | 'cash' | 'pact' | 'clinic',
  note: ''
}

export function TopUps() {
  const { profile } = useAuth()
  const [topups, setTopups] = useState<ExtendedTopUp[]>([])
  const [students, setStudents] = useState<Student[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [original, setOriginal] = useState<{ studentId: string; minutesAdded: number } | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<ExtendedTopUp | null>(null)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [showColumnFilter, setShowColumnFilter] = useState(false)
  const [visibleColumns, setVisibleColumns] = useState({
    date: true,
    student: true,
    minutes: true,
    amount: true,
    method: true,
    enteredBy: true,
    note: true
  })
  const [formData, setFormData] = useState(emptyForm)
  const [toast, setToast] = useState<string | null>(null)

  const enteredBy = profile?.email || 'Admin'
  const enteredByRole = profile?.role || 'admin'

  useEffect(() => {
    fetchTopups()
    fetchStudents()
  }, [])

  // Auto-dismiss the hint toast.
  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(null), 5000)
    return () => clearTimeout(t)
  }, [toast])

  const fetchTopups = async () => {
    try {
      const { data, error } = await supabase
        .from('topups')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error

      const formattedTopups = data?.map(topup => ({
        id: topup.id,
        studentId: topup.student_id,
        minutesAdded: topup.minutes_added,
        amountUsd: topup.amount_usd,
        createdAt: topup.created_at,
        method: topup.method,
        enteredBy: topup.entered_by,
        note: topup.note
      })) || []

      setTopups(formattedTopups)
    } catch (error) {
      console.error('Error fetching topups:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchStudents = async () => {
    try {
      const { data } = await supabase
        .from('students')
        .select('id, name, session_length_minutes')
        .order('name')
      // Price per session is money — admin-only student_billing table (Top-ups is admin-only).
      const { data: billing } = await supabase.from('student_billing').select('student_id, price_per_session')
      const priceMap = new Map((billing || []).map(b => [b.student_id, b.price_per_session]))
      setStudents(data?.map(s => ({
        id: s.id,
        name: s.name,
        tutorId: '',
        balanceMinutes: 0,
        pricePerSession: priceMap.get(s.id) ?? null,
        sessionLengthMinutes: s.session_length_minutes
      })) || [])
    } catch (error) {
      console.error('Error fetching students:', error)
    }
  }

  // Adjust a student's balance by `delta` minutes and record a ledger entry.
  // No-op when delta is 0 (common — most top-ups are payment records with +0 min).
  const adjustStudentBalance = async (studentId: string, delta: number, reference: string) => {
    if (!delta) return
    const { data: studentData, error: fetchError } = await supabase
      .from('students')
      .select('balance_minutes')
      .eq('id', studentId)
      .single()
    if (fetchError) throw fetchError

    const newBalance = (studentData?.balance_minutes || 0) + delta
    const { error: balanceError } = await supabase
      .from('students')
      .update({ balance_minutes: newBalance })
      .eq('id', studentId)
    if (balanceError) throw balanceError

    await supabase.from('minutes_ledger').insert({
      student_id: studentId,
      delta_minutes: delta,
      reason: 'topup',
      related_id: studentId,
      running_balance: newBalance,
      entered_by: enteredBy,
      entered_by_role: enteredByRole,
      reference
    })

    // Keep the low-balance alert in sync (clears once topped back up).
    await notifyLowBalance(studentId, newBalance, getStudentName(studentId))
  }

  const openAddModal = () => {
    setEditingId(null)
    setOriginal(null)
    setFormError(null)
    setFormData(emptyForm)
    setShowModal(true)
  }

  const openEditModal = (topup: ExtendedTopUp) => {
    setEditingId(topup.id)
    setOriginal({ studentId: topup.studentId, minutesAdded: topup.minutesAdded })
    setFormError(null)
    setFormData({
      studentId: topup.studentId,
      inputMode: 'manual',
      sessions: '',
      minutesAdded: topup.minutesAdded,
      amountUsd: topup.amountUsd,
      method: topup.method,
      note: topup.note || ''
    })
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingId(null)
    setOriginal(null)
    setFormError(null)
    setFormData(emptyForm)
  }

  const handleSaveTopup = async () => {
    if (!formData.studentId) {
      setFormError('Please select a student.')
      return
    }
    setSaving(true)
    setFormError(null)
    try {
      if (editingId && original) {
        // Update the top-up record
        const { error: updateError } = await supabase
          .from('topups')
          .update({
            student_id: formData.studentId,
            minutes_added: formData.minutesAdded,
            amount_usd: formData.amountUsd,
            method: formData.method,
            note: formData.note
          })
          .eq('id', editingId)
        if (updateError) throw updateError

        // Reconcile minute balances + ledger for any change in minutes/student
        if (original.studentId === formData.studentId) {
          const delta = formData.minutesAdded - original.minutesAdded
          await adjustStudentBalance(formData.studentId, delta, 'Top-up edited - minutes adjusted')
        } else {
          await adjustStudentBalance(original.studentId, -original.minutesAdded, 'Top-up reassigned - minutes removed')
          await adjustStudentBalance(formData.studentId, formData.minutesAdded, 'Top-up reassigned - minutes added')
        }
      } else {
        // Create the top-up record
        const { error: topupError } = await supabase.from('topups').insert({
          student_id: formData.studentId,
          minutes_added: formData.minutesAdded,
          amount_usd: formData.amountUsd,
          method: formData.method,
          note: formData.note,
          entered_by: enteredBy
        })
        if (topupError) throw topupError

        await adjustStudentBalance(formData.studentId, formData.minutesAdded, 'Top-up added')

        // Activity + admin notification for the new top-up.
        const studentName = getStudentName(formData.studentId)
        const actor = profile ? { id: profile.id, email: profile.email, role: profile.role } : null
        await logActivity({
          scope: 'topups',
          action: 'topup_added',
          summary: `Top-up for ${studentName}: $${formData.amountUsd.toFixed(2)}${formData.minutesAdded ? ` (+${formData.minutesAdded} min)` : ''}`,
          entityTable: 'topups',
          entityId: formData.studentId,
          metadata: { studentId: formData.studentId, amount: formData.amountUsd, minutes: formData.minutesAdded },
          actor,
        })
        await createNotification({
          type: 'topup',
          title: `Top-up recorded for ${studentName}`,
          message: `$${formData.amountUsd.toFixed(2)}${formData.minutesAdded ? ` · +${formData.minutesAdded} min` : ''} · ${formData.method}`,
          relatedTable: 'topups',
          relatedId: formData.studentId,
          route: '/dashboard/topups',
          severity: 'info',
        })
      }

      closeModal()
      fetchTopups()
    } catch (error: any) {
      console.error('Error saving topup:', error)
      setFormError(error?.message || 'Failed to save top-up. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return
    setSaving(true)
    try {
      // Reverse any minutes this top-up added back out of the student's balance
      await adjustStudentBalance(deleteTarget.studentId, -deleteTarget.minutesAdded, 'Top-up deleted - minutes removed')

      const { error: deleteError } = await supabase
        .from('topups')
        .delete()
        .eq('id', deleteTarget.id)
      if (deleteError) throw deleteError

      setDeleteTarget(null)
      fetchTopups()
    } catch (error) {
      console.error('Error deleting topup:', error)
      setFormError('Failed to delete top-up.')
    } finally {
      setSaving(false)
    }
  }

  const getStudentName = (id: string) => students.find(s => s.id === id)?.name || 'Unknown'

  // ---- Auto-minute calculation (V3) ----------------------------------
  // Minutes & amount are always derived into formData.minutesAdded / amountUsd
  // (the canonical stored values), so the save/edit/delete logic is untouched.
  const selectedStudent = students.find(s => s.id === formData.studentId)
  const studentPrice = selectedStudent?.pricePerSession ?? null
  const studentLength = getSessionLength(selectedStudent?.sessionLengthMinutes)
  const hasPrice = studentPrice != null && studentPrice > 0

  const handleStudentSelect = (studentId: string) => {
    const st = students.find(s => s.id === studentId)
    const p = st?.pricePerSession ?? null
    const l = getSessionLength(st?.sessionLengthMinutes)
    const noPrice = p == null || p <= 0

    // If they're in Amount mode and pick a student with no price, gently fall
    // back to Sessions mode (which still works) and explain why.
    if (formData.inputMode === 'amount' && noPrice) {
      setToast(`${st?.name || 'This student'} has no price per session set yet — switched to Sessions mode. Add a price on their profile to use Amount mode.`)
      const { minutes } = fromSessions(parseFloat(formData.sessions) || 0, p, l)
      setFormData(f => ({ ...f, studentId, inputMode: 'sessions', minutesAdded: minutes }))
      return
    }
    if (formData.inputMode === 'sessions') {
      const { minutes, amount } = fromSessions(parseFloat(formData.sessions) || 0, p, l)
      setFormData(f => ({ ...f, studentId, minutesAdded: minutes, amountUsd: p != null ? amount : f.amountUsd }))
      return
    }
    if (formData.inputMode === 'amount') {
      const { minutes } = fromAmount(formData.amountUsd, p, l)
      setFormData(f => ({ ...f, studentId, minutesAdded: minutes }))
      return
    }
    setFormData(f => ({ ...f, studentId }))
  }

  const handleModeSelect = (mode: InputMode) => {
    // Amount mode needs a price per session. Hint instead of silently doing nothing.
    if (mode === 'amount' && !hasPrice) {
      setToast(formData.studentId
        ? `${getStudentName(formData.studentId)} has no price per session set yet. Add one on their profile to use Amount mode — or use Sessions / Manual for now.`
        : 'Pick a student that has a price per session to use Amount mode.')
      return
    }
    setFormData(f => {
      if (mode === 'sessions') {
        const { minutes, amount } = fromSessions(parseFloat(f.sessions) || 0, studentPrice, studentLength)
        return { ...f, inputMode: mode, minutesAdded: minutes, amountUsd: studentPrice != null ? amount : f.amountUsd }
      }
      return { ...f, inputMode: mode }
    })
  }

  const handleSessionsInput = (val: string) => {
    const { minutes, amount } = fromSessions(parseFloat(val) || 0, studentPrice, studentLength)
    setFormData(f => ({ ...f, sessions: val, minutesAdded: minutes, amountUsd: studentPrice != null ? amount : f.amountUsd }))
  }

  const handleAmountInput = (val: string) => {
    const amt = parseFloat(val) || 0
    setFormData(f => {
      if (f.inputMode === 'amount') {
        const { minutes } = fromAmount(amt, studentPrice, studentLength)
        return { ...f, amountUsd: amt, minutesAdded: minutes }
      }
      return { ...f, amountUsd: amt }
    })
  }

  // Sessions implied by the amount entered (for the "by amount" readout).
  const impliedSessions = hasPrice ? fromAmount(formData.amountUsd, studentPrice, studentLength).sessions : null

  const enhancedTopups = topups.map(topup => ({
    ...topup,
    studentName: getStudentName(topup.studentId)
  }))

  const exportToCSV = () => {
    const csvData = enhancedTopups.map(topup => {
      const row: any = {}
      if (visibleColumns.date) {
        row.Date = format(new Date(topup.createdAt), 'MMM d, yyyy h:mm a')
      }
      if (visibleColumns.student) {
        row.Student = topup.studentName
      }
      if (visibleColumns.minutes) {
        row['Minutes Added'] = topup.minutesAdded
      }
      if (visibleColumns.amount) {
        row['Amount (USD)'] = `$${topup.amountUsd.toFixed(2)}`
      }
      if (visibleColumns.method) {
        row['Payment Method'] = topup.method || 'N/A'
      }
      if (visibleColumns.enteredBy) {
        row['Entered By'] = topup.enteredBy || 'System'
      }
      if (visibleColumns.note) {
        row.Note = topup.note || ''
      }
      return row
    })

    if (csvData.length === 0) {
      alert('No data to export')
      return
    }

    const headers = Object.keys(csvData[0] || {})
    const csvContent = [
      headers.join(','),
      ...csvData.map(row =>
        headers.map(header =>
          `"${String(row[header as keyof typeof row] || '').replace(/"/g, '""')}"`
        ).join(',')
      )
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `topups_${format(new Date(), 'yyyy-MM-dd')}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }

  const getMethodIcon = (method: string) => {
    switch (method) {
      case 'card':
        return <CreditCard className="h-4 w-4" />
      case 'bank':
        return <Building className="h-4 w-4" />
      case 'cash':
        return <Banknote className="h-4 w-4" />
      case 'pact':
      case 'clinic':
        return <DollarSign className="h-4 w-4" />
      default:
        return <DollarSign className="h-4 w-4" />
    }
  }

  const getMethodColor = (method: string) => {
    switch (method) {
      case 'card':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100'
      case 'bank':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-100'
      case 'cash':
        return 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
      case 'pact':
      case 'clinic':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-800 dark:text-orange-100'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-400"></div>
      </div>
    )
  }

  const isEditing = editingId !== null

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Top-ups</h1>
        <div className="flex flex-wrap gap-2">
          <ActivityClock scope="topups" />
          <button
            onClick={exportToCSV}
            className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <FileDown className="h-5 w-5 mr-2" />
            Export CSV
          </button>
          <div className="relative">
            <button
              onClick={() => setShowColumnFilter(!showColumnFilter)}
              className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <Filter className="h-5 w-5 mr-2" />
              Columns
              <ChevronDown className="h-4 w-4 ml-1" />
            </button>
            {showColumnFilter && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-surface-100 rounded-lg shadow-lg z-10 p-3">
                <div className="space-y-2">
                  {Object.entries(visibleColumns).map(([key, value]) => (
                    <label key={key} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={(e) => setVisibleColumns({ ...visibleColumns, [key]: e.target.checked })}
                        className="w-4 h-4 text-brand-600 rounded focus:ring-brand-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 capitalize">
                        {key === 'enteredBy' ? 'Entered By' : key}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <button
            onClick={openAddModal}
            className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-800 transition-colors"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Top-up
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white dark:bg-surface-100 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Top-ups</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{topups.length}</p>
            </div>
            <DollarSign className="h-8 w-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white dark:bg-surface-100 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                ${topups.reduce((sum, t) => sum + t.amountUsd, 0).toFixed(2)}
              </p>
            </div>
            <DollarSign className="h-8 w-8 text-brand-400" />
          </div>
        </div>
        <div className="bg-white dark:bg-surface-100 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Minutes</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {topups.reduce((sum, t) => sum + t.minutesAdded, 0).toLocaleString()}
              </p>
            </div>
            <DollarSign className="h-8 w-8 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Topups Table */}
      <div className="bg-white dark:bg-surface-100 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-surface-0">
            <tr>
              {visibleColumns.date && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Date
                </th>
              )}
              {visibleColumns.student && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Student
                </th>
              )}
              {visibleColumns.minutes && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Minutes
                </th>
              )}
              {visibleColumns.amount && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Amount
                </th>
              )}
              {visibleColumns.method && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Method
                </th>
              )}
              {visibleColumns.enteredBy && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Entered By
                </th>
              )}
              {visibleColumns.note && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Note
                </th>
              )}
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-surface-100 divide-y divide-gray-200 dark:divide-gray-700">
            {enhancedTopups.map((topup) => (
              <tr key={topup.id} className="group hover:bg-gray-50 dark:hover:bg-surface-0/60 transition-colors">
                {visibleColumns.date && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {format(new Date(topup.createdAt), 'MMM d, yyyy h:mm a')}
                  </td>
                )}
                {visibleColumns.student && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {topup.studentName}
                  </td>
                )}
                {visibleColumns.minutes && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    +{topup.minutesAdded} min
                  </td>
                )}
                {visibleColumns.amount && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600 dark:text-green-400">
                    ${topup.amountUsd.toFixed(2)}
                  </td>
                )}
                {visibleColumns.method && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getMethodColor(topup.method)}`}>
                      {getMethodIcon(topup.method)}
                      <span className="ml-1">{topup.method}</span>
                    </span>
                  </td>
                )}
                {visibleColumns.enteredBy && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {topup.enteredBy || 'System'}
                  </td>
                )}
                {visibleColumns.note && (
                  <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                    {topup.note || '-'}
                  </td>
                )}
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                  {hasAdminRights(profile?.role) && (
                  <div className="flex items-center justify-end gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => openEditModal(topup)}
                      title="Edit top-up"
                      className="p-2 rounded-lg text-gray-500 hover:text-brand-500 hover:bg-brand-500/10 dark:text-gray-400 transition-colors"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setDeleteTarget(topup)}
                      title="Delete top-up"
                      className="p-2 rounded-lg text-gray-500 hover:text-red-500 hover:bg-red-500/10 dark:text-gray-400 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {enhancedTopups.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500 dark:text-gray-400">No top-ups found</p>
          </div>
        )}
      </div>

      {/* Add / Edit Topup Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white dark:bg-surface-100 rounded-lg p-6 w-full max-w-md shadow-2xl animate-modalIn">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {isEditing ? 'Edit Top-up' : 'Add Top-up'}
                </h2>
                {isEditing && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Changing minutes will adjust the student's balance.
                  </p>
                )}
              </div>
              <button
                onClick={closeModal}
                className="p-1 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-surface-0 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Student</label>
                <select
                  value={formData.studentId}
                  onChange={(e) => handleStudentSelect(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-0 text-gray-900 dark:text-white"
                >
                  <option value="">Select Student</option>
                  {students.map(student => (
                    <option key={student.id} value={student.id}>{student.name}</option>
                  ))}
                </select>
              </div>
              {/* Input mode selector — auto-fill minutes from sessions or amount */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Entry method</label>
                  {formData.studentId && (
                    <span className="text-[11px] text-gray-400 dark:text-gray-500 flex items-center gap-1">
                      <Timer className="h-3 w-3" />
                      {hasPrice ? `${formatMoney(studentPrice)} · ${studentLength} min/session` : `${studentLength} min/session · no price set`}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-1 rounded-lg bg-gray-100 dark:bg-surface-0 p-1">
                  <ModeTab active={formData.inputMode === 'sessions'} onClick={() => handleModeSelect('sessions')} icon={Hash} label="Sessions" />
                  <ModeTab active={formData.inputMode === 'amount'} onClick={() => handleModeSelect('amount')} icon={DollarSign} label="Amount" muted={!hasPrice} title={!hasPrice ? 'Set a price per session for this student to use this mode' : undefined} />
                  <ModeTab active={formData.inputMode === 'manual'} onClick={() => handleModeSelect('manual')} icon={Pencil} label="Manual" />
                </div>
              </div>

              {/* Sessions mode */}
              {formData.inputMode === 'sessions' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Number of sessions</label>
                  <input
                    type="number" min="0" step="1" placeholder="e.g. 5"
                    value={formData.sessions}
                    onChange={(e) => handleSessionsInput(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-0 text-gray-900 dark:text-white"
                  />
                  <CalcReadout
                    minutes={formData.minutesAdded}
                    amount={hasPrice ? formData.amountUsd : null}
                    note={!hasPrice ? "Set this student's price per session to auto-fill the amount." : undefined}
                  />
                </div>
              )}

              {/* Amount mode */}
              {formData.inputMode === 'amount' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Amount paid (USD)</label>
                  <input
                    type="number" min="0" step="0.01" placeholder="Enter amount in USD"
                    value={formData.amountUsd || ''}
                    onChange={(e) => handleAmountInput(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-0 text-gray-900 dark:text-white"
                  />
                  <CalcReadout minutes={formData.minutesAdded} sessions={impliedSessions} />
                </div>
              )}

              {/* Manual mode — type minutes & amount directly (used for edits / custom) */}
              {formData.inputMode === 'manual' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Minutes to Add</label>
                    <input
                      type="number"
                      placeholder="Enter number of minutes"
                      value={formData.minutesAdded}
                      onChange={(e) => setFormData({ ...formData, minutesAdded: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-0 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Amount (USD)</label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Enter amount in USD"
                      value={formData.amountUsd}
                      onChange={(e) => setFormData({ ...formData, amountUsd: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-0 text-gray-900 dark:text-white"
                    />
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Payment Method</label>
                <select
                  value={formData.method}
                  onChange={(e) => setFormData({ ...formData, method: e.target.value as 'card' | 'bank' | 'cash' | 'pact' | 'clinic' })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-0 text-gray-900 dark:text-white"
                >
                  <option value="card">Credit Card</option>
                  <option value="bank">Bank Transfer</option>
                  <option value="cash">Cash</option>
                  <option value="pact">PACT</option>
                  <option value="clinic">Clinic-funded (other clinic)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Note (Optional)</label>
                <textarea
                  placeholder="Add any notes here"
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-0 text-gray-900 dark:text-white"
                  rows={3}
                />
              </div>
              {formError && (
                <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>
              )}
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={closeModal}
                disabled={saving}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-surface-0 text-gray-700 dark:text-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTopup}
                disabled={saving}
                className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-800 disabled:opacity-60"
              >
                {saving ? 'Saving…' : isEditing ? 'Save Changes' : 'Add Top-up'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white dark:bg-surface-100 rounded-lg p-6 w-full max-w-md shadow-2xl animate-modalIn">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/10">
                <AlertTriangle className="h-5 w-5 text-red-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Delete Top-up</h2>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Delete the <span className="font-semibold text-gray-900 dark:text-white">${deleteTarget.amountUsd.toFixed(2)}</span>{' '}
              top-up for <span className="font-semibold text-gray-900 dark:text-white">{deleteTarget.studentName}</span>?
            </p>
            {deleteTarget.minutesAdded > 0 && (
              <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
                This will remove {deleteTarget.minutesAdded} minutes from {deleteTarget.studentName}'s balance.
              </p>
            )}
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">This action cannot be undone.</p>
            {formError && (
              <p className="mt-3 text-sm text-red-600 dark:text-red-400">{formError}</p>
            )}
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => { setDeleteTarget(null); setFormError(null) }}
                disabled={saving}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-surface-0 text-gray-700 dark:text-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={saving}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-60"
              >
                {saving ? 'Deleting…' : 'Delete Top-up'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hint toast */}
      {toast && (
        <div className="fixed inset-x-0 bottom-6 z-[60] flex justify-center px-4 pointer-events-none">
          <div className="pointer-events-auto flex items-start gap-2.5 max-w-md rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 px-4 py-3 shadow-lg animate-modalIn">
            <Info className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800 dark:text-amber-200">{toast}</p>
            <button onClick={() => setToast(null)} className="ml-1 flex-shrink-0 text-amber-500 hover:text-amber-700 dark:hover:text-amber-300">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ---- Top-up form helpers ----------------------------------------------

function ModeTab({ active, onClick, icon: Icon, label, muted, title }: {
  active: boolean; onClick: () => void; icon: typeof Hash; label: string; muted?: boolean; title?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-sm font-medium transition-colors ${
        active
          ? 'bg-white dark:bg-surface-100 text-gray-900 dark:text-white shadow-sm'
          : muted
            ? 'text-gray-400 dark:text-gray-600 hover:text-gray-600 dark:hover:text-gray-400'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
      }`}
    >
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  )
}

function CalcReadout({ minutes, amount, sessions, note }: {
  minutes: number; amount?: number | null; sessions?: number | null; note?: string
}) {
  return (
    <div className="mt-2">
      <div className="flex items-center gap-2.5 rounded-lg border border-brand-200/70 dark:border-brand-900/40 bg-brand-50/70 dark:bg-brand-900/15 px-3 py-2.5">
        <Calculator className="h-4 w-4 text-brand-500 flex-shrink-0" />
        <div className="text-sm text-gray-700 dark:text-gray-200">
          <span className="font-semibold text-gray-900 dark:text-white">{minutes.toLocaleString()} min</span>
          <span className="text-gray-400"> will be added</span>
          {amount != null && (
            <> · <span className="font-semibold text-gray-900 dark:text-white">{formatMoney(amount)}</span></>
          )}
          {sessions != null && (
            <> · ≈ <span className="font-semibold text-gray-900 dark:text-white">{sessions}</span> session{sessions === 1 ? '' : 's'}</>
          )}
        </div>
      </div>
      {note && <p className="mt-1.5 text-[11px] text-amber-600 dark:text-amber-400">{note}</p>}
    </div>
  )
}
